import { Button, Tooltip } from 'antd';
import { CloseOutlined, ColumnHeightOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/useTheme';
import { TIO_PRECIPITATION_GRADIENT, TIO_SNOW_GRADIENT, TIO_FREEZING_RAIN_GRADIENT, TIO_SLEET_GRADIENT, MARSHALL_PALMER_SCALE } from './nowcastingConstants';
import { isTioProvider, type MapTileProvider } from './useNowcastingState';
import { getColormapById } from './numericColormaps';
import type { TioTileField } from '../../api/tomorrowIoTilesService';

const MP_PRECIP_FIELDS: TioTileField[] = ['precipitationIntensity', 'rainIntensity'];

export interface RadarLegendProps {
  mapTileProvider: MapTileProvider;
  tioTileField?: TioTileField;
  isRainOnlyMode: boolean;
  numericColormap?: string;
  compact?: boolean;
  onCompactChange?: (compact: boolean) => void;
  onClose?: () => void;
}

function renderTioGradientBar(
  gradient: typeof TIO_PRECIPITATION_GRADIENT,
  height: number,
  width: number,
  theme: string,
) {
  return (
    <div
      style={{
        width,
        height,
        background: `linear-gradient(to bottom, ${gradient
          .map((colorStop, index, array) => {
            const percentage = (index / (array.length - 1)) * 100;
            const opacity = colorStop.opacity / 255;
            const r = parseInt(colorStop.hexColor.slice(1, 3), 16);
            const g = parseInt(colorStop.hexColor.slice(3, 5), 16);
            const b = parseInt(colorStop.hexColor.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${opacity}) ${percentage}%`;
          })
          .join(', ')})`,
        borderRadius: 4,
        border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
      }}
    />
  );
}

function MpLegendContent({ scale, theme, compact }: {
  scale: typeof MARSHALL_PALMER_SCALE;
  theme: string;
  compact: boolean;
}) {
  const BLOCK_H = compact ? 16 : 28;
  const reversed = [...scale].reverse();
  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)';
  const tickColor = theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)';
  const labelColor = theme === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)';
  const totalHeight = reversed.length * BLOCK_H;
  const colorBarWidth = compact ? 14 : 22;

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {reversed.map((band, idx) => (
            <div
              key={idx}
              style={{
                width: colorBarWidth,
                height: BLOCK_H,
                backgroundColor: band.color,
                borderLeft: `1px solid ${borderColor}`,
                borderRight: `1px solid ${borderColor}`,
                borderTop: `1px solid ${borderColor}`,
                borderBottom: idx === reversed.length - 1 ? `1px solid ${borderColor}` : undefined,
                borderRadius: idx === 0 ? '4px 4px 0 0' : idx === reversed.length - 1 ? '0 0 4px 4px' : 0,
              }}
            />
          ))}
        </div>

        {reversed.map((band, idx) => (
          <div
            key={`tick-${idx}`}
            style={{
              position: 'absolute',
              top: idx * BLOCK_H,
              left: colorBarWidth + 1,
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <div style={{ width: compact ? 4 : 6, height: 1, backgroundColor: tickColor }} />
            <span style={{ fontSize: compact ? 9 : 11, fontWeight: 700, marginLeft: compact ? 2 : 3, color: labelColor }}>
              {band.label}
            </span>
          </div>
        ))}
      </div>

      {!compact && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingLeft: 32,
          height: totalHeight,
        }}>
          <span style={{
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.5,
            color: theme === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)',
            whiteSpace: 'nowrap',
          }}>
            Rain Rate (mm/h)
          </span>
        </div>
      )}
    </div>
  );
}

function TioLegendContent({ theme, effectiveRainOnlyMode, compact }: {
  theme: string;
  effectiveRainOnlyMode: boolean;
  compact: boolean;
}) {
  const barHeight = compact ? 180 : 360;
  const barWidth = compact ? 14 : 20;
  const labelStyle = {
    fontSize: compact ? 9 : 11,
    fontWeight: 600 as const,
    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
    textAlign: 'center' as const,
    lineHeight: '1.2',
  };
  const fixedLabelHeight = effectiveRainOnlyMode ? 'auto' : compact ? '20px' : '28px';

  return (
    <div style={{ display: 'flex', gap: compact ? 10 : 20, alignItems: 'flex-start', justifyContent: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: barWidth }}>
        <div style={{ ...labelStyle, marginBottom: compact ? 3 : 6, height: fixedLabelHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Rain
        </div>
        {renderTioGradientBar(TIO_PRECIPITATION_GRADIENT, barHeight, barWidth, theme)}
      </div>

      {!effectiveRainOnlyMode && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: barWidth }}>
            <div style={{ ...labelStyle, marginBottom: compact ? 3 : 6, height: fixedLabelHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Snow
            </div>
            {renderTioGradientBar(TIO_SNOW_GRADIENT, barHeight, barWidth, theme)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: barWidth }}>
            <div style={{
              ...labelStyle,
              marginBottom: compact ? 3 : 6,
              height: fixedLabelHeight,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <div>{compact ? 'Frz.' : 'Freezing'}</div>
              <div>Rain</div>
            </div>
            {renderTioGradientBar(TIO_FREEZING_RAIN_GRADIENT, barHeight, barWidth, theme)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: barWidth }}>
            <div style={{ ...labelStyle, marginBottom: compact ? 3 : 6, height: fixedLabelHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Sleet
            </div>
            {renderTioGradientBar(TIO_SLEET_GRADIENT, barHeight, barWidth, theme)}
          </div>
        </>
      )}

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ ...labelStyle, marginBottom: compact ? 3 : 6 }}>mm/h</div>
        {TIO_PRECIPITATION_GRADIENT.map((colorStop, index) => {
          const displayValue = colorStop.value === 0
            ? '0'
            : colorStop.value < 1
              ? colorStop.value.toFixed(2)
              : Math.round(colorStop.value).toString();
          return (
            <div key={index} style={{
              fontSize: compact ? 8 : 10,
              height: barHeight / TIO_PRECIPITATION_GRADIENT.length,
              display: 'flex',
              alignItems: 'center',
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
            }}>
              {displayValue}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const RadarLegend = ({
  mapTileProvider,
  tioTileField,
  isRainOnlyMode,
  numericColormap,
  compact = false,
  onCompactChange,
  onClose,
}: RadarLegendProps) => {
  const { theme } = useTheme();

  const tioUsingMpGradient = isTioProvider(mapTileProvider) && !!tioTileField && MP_PRECIP_FIELDS.includes(tioTileField);
  const showMpLegend = mapTileProvider === 'radar-l2' || mapTileProvider === 'radar' || mapTileProvider === 'radar-l2-numeric' || tioUsingMpGradient;
  const showTioLegend = !showMpLegend && isTioProvider(mapTileProvider);

  const effectiveRainOnlyMode = (mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') ? true : isRainOnlyMode;

  const activeScale = mapTileProvider === 'radar-l2-numeric' && numericColormap
    ? getColormapById(numericColormap).scale
    : MARSHALL_PALMER_SCALE;

  return (
    <div style={{
      backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
      color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
      padding: compact ? '6px' : '12px',
      borderRadius: compact ? 8 : 12,
      minWidth: compact
        ? undefined
        : showMpLegend
          ? 120
          : showTioLegend
            ? (effectiveRainOnlyMode ? 160 : 280)
            : 200,
      border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      backdropFilter: 'blur(10px)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: compact ? 8 : 16,
        paddingBottom: compact ? 6 : 12,
        borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
        userSelect: 'none',
        gap: 4,
      }}>
        {!compact && (
          <div style={{
            fontSize: 16,
            fontWeight: 600,
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
          }}>
            Legend
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onCompactChange && (
            <Tooltip title={compact ? 'Expand legend' : 'Compact legend'}>
              <Button
                type="text"
                size="small"
                icon={<ColumnHeightOutlined style={compact ? { fontSize: 12 } : undefined} />}
                onClick={() => onCompactChange(!compact)}
                style={{
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                  minWidth: compact ? 20 : 24,
                  width: compact ? 20 : undefined,
                  height: compact ? 20 : undefined,
                  padding: '0 4px',
                }}
              />
            </Tooltip>
          )}
          {onClose && (
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined style={compact ? { fontSize: 10 } : undefined} />}
              onClick={onClose}
              style={{
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                minWidth: compact ? 20 : 24,
                width: compact ? 20 : undefined,
                height: compact ? 20 : undefined,
                padding: '0 4px',
              }}
            />
          )}
        </div>
      </div>

      {/* Legend Content */}
      {showMpLegend && (
        <MpLegendContent scale={activeScale} theme={theme} compact={compact} />
      )}
      {showTioLegend && (
        <TioLegendContent theme={theme} effectiveRainOnlyMode={effectiveRainOnlyMode} compact={compact} />
      )}
    </div>
  );
};
